import { brandColors, brandCssVariables, brandInteractionTokens } from '../../shared/brand-colors';
import type { ThemeConfig } from 'antd';

export const frontFontFamily =
  '-apple-system, BlinkMacSystemFont, "PingFang SC", "HarmonyOS Sans SC", "Noto Sans CJK SC", "Noto Sans SC", "Source Han Sans SC", "Microsoft YaHei UI", "Microsoft YaHei", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

export const frontTypographyTokens = {
  headlineLg: {
    fontSize: 32,
    fontWeight: 800,
    lineHeight: 40,
    letterSpacing: 0
  },
  headlineLgMobile: {
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 32,
    letterSpacing: 0
  },
  headlineMd: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 24,
    letterSpacing: 0
  },
  bodyLg: {
    fontSize: 17,
    fontWeight: 400,
    lineHeight: 24,
    letterSpacing: 0
  },
  bodyMd: {
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 20,
    letterSpacing: 0
  },
  labelLg: {
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 20,
    letterSpacing: 0
  },
  labelMd: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 16,
    letterSpacing: 0
  }
} as const;

export const frontDesignTokens = {
  colorBg: '#ffffff',
  colorPanel: '#ffffff',
  colorPanelSoft: '#f8f8f8',
  colorBorder: 'rgba(34, 36, 38, 0.15)',
  colorBorderSoft: 'rgba(34, 36, 38, 0.1)',
  colorText: '#1b1c1d',
  colorTextSecondary: '#535e6b',
  colorTextTertiary: '#6b7280',
  colorPrimary: brandColors.primary,
  colorPrimaryActive: brandColors.active,
  colorSuccess: '#21ba45',
  colorWarning: '#f2711c',
  colorPurple: '#a333c8',
  colorError: '#db2828',
  colorSurfaceHover: '#f8f8f8',
  borderRadius: 8,
  controlHeight: 36,
  controlHeightLarge: 44,
  controlHeightSmall: 28,
  shadow: 'none',
  shadowHover: 'none',
  focusRing: `0 0 0 3px ${brandColors.focus}`
} as const;

export const frontTheme: ThemeConfig = {
  token: {
    ...brandInteractionTokens,
    colorSuccess: frontDesignTokens.colorSuccess,
    colorWarning: frontDesignTokens.colorWarning,
    colorError: frontDesignTokens.colorError,
    colorInfo: "#2185d0",
    colorText: frontDesignTokens.colorText,
    colorTextSecondary: frontDesignTokens.colorTextSecondary,
    colorTextTertiary: frontDesignTokens.colorTextTertiary,
    colorBgLayout: frontDesignTokens.colorBg,
    colorBgContainer: frontDesignTokens.colorPanel,
    colorBgElevated: frontDesignTokens.colorPanel,
    colorBorder: frontDesignTokens.colorBorder,
    colorBorderSecondary: frontDesignTokens.colorBorderSoft,
    borderRadius: frontDesignTokens.borderRadius,
    borderRadiusLG: frontDesignTokens.borderRadius,
    borderRadiusSM: 6,
    controlHeight: frontDesignTokens.controlHeight,
    controlHeightLG: frontDesignTokens.controlHeightLarge,
    controlHeightSM: frontDesignTokens.controlHeightSmall,
    fontFamily: frontFontFamily,
    fontSize: frontTypographyTokens.bodyMd.fontSize,
    fontSizeSM: frontTypographyTokens.labelMd.fontSize,
    fontSizeLG: frontTypographyTokens.bodyLg.fontSize,
    fontSizeHeading1: frontTypographyTokens.headlineLg.fontSize,
    fontSizeHeading2: frontTypographyTokens.headlineLgMobile.fontSize,
    fontSizeHeading3: frontTypographyTokens.headlineMd.fontSize,
    fontSizeHeading4: frontTypographyTokens.bodyLg.fontSize,
    fontSizeHeading5: frontTypographyTokens.labelLg.fontSize,
    lineHeight: frontTypographyTokens.bodyMd.lineHeight / frontTypographyTokens.bodyMd.fontSize,
    lineHeightSM: frontTypographyTokens.labelMd.lineHeight / frontTypographyTokens.labelMd.fontSize,
    lineHeightLG: frontTypographyTokens.bodyLg.lineHeight / frontTypographyTokens.bodyLg.fontSize,
    lineHeightHeading1: frontTypographyTokens.headlineLg.lineHeight / frontTypographyTokens.headlineLg.fontSize,
    lineHeightHeading2: frontTypographyTokens.headlineLgMobile.lineHeight / frontTypographyTokens.headlineLgMobile.fontSize,
    lineHeightHeading3: frontTypographyTokens.headlineMd.lineHeight / frontTypographyTokens.headlineMd.fontSize,
    lineHeightHeading4: frontTypographyTokens.bodyLg.lineHeight / frontTypographyTokens.bodyLg.fontSize,
    lineHeightHeading5: frontTypographyTokens.labelLg.lineHeight / frontTypographyTokens.labelLg.fontSize,
    fontWeightStrong: 700,
    boxShadow: frontDesignTokens.shadow,
    boxShadowSecondary: frontDesignTokens.shadowHover
  },
  components: {
    Layout: {
      bodyBg: '#ffffff',
      headerBg: '#ffffff'
    },
    Button: {
      borderRadius: 4,
      fontWeight: 700,
      primaryShadow: 'none'
    },
    Card: {
      borderRadiusLG: 8,
      headerBg: '#ffffff'
    },
    Input: {
      borderRadius: frontDesignTokens.borderRadius,
      activeShadow: frontDesignTokens.focusRing
    },
    InputNumber: {
      borderRadius: frontDesignTokens.borderRadius,
      activeShadow: frontDesignTokens.focusRing
    },
    Select: {
      borderRadius: frontDesignTokens.borderRadius,
      activeBorderColor: frontDesignTokens.colorPrimary,
      activeOutlineColor: brandColors.focus
    },
    DatePicker: {
      borderRadius: frontDesignTokens.borderRadius,
      activeShadow: frontDesignTokens.focusRing
    },
    Table: {
      headerBg: '#f8f8f8',
      headerColor: frontDesignTokens.colorTextTertiary,
      rowHoverBg: frontDesignTokens.colorSurfaceHover,
      borderColor: frontDesignTokens.colorBorder,
      cellPaddingBlock: 12,
      cellPaddingInline: 14
    },
    Modal: {
      borderRadiusLG: 8,
      titleFontSize: 18
    },
    Menu: {
      itemBorderRadius: 6,
      itemSelectedBg: brandColors.soft,
      itemSelectedColor: frontDesignTokens.colorPrimaryActive
    },
    Tabs: {
      itemSelectedColor: frontDesignTokens.colorPrimaryActive,
      inkBarColor: frontDesignTokens.colorPrimary
    },
    Tag: {
      borderRadiusSM: 6
    }
  }
};

const frontCssVariables: Record<string, string> = {
  ...brandCssVariables,
  '--bc-bg': frontDesignTokens.colorBg,
  '--bc-panel': frontDesignTokens.colorPanel,
  '--bc-panel-soft': frontDesignTokens.colorPanelSoft,
  '--bc-border': frontDesignTokens.colorBorder,
  '--bc-border-soft': frontDesignTokens.colorBorderSoft,
  '--bc-text': frontDesignTokens.colorText,
  '--bc-text-2': frontDesignTokens.colorTextSecondary,
  '--bc-text-3': frontDesignTokens.colorTextTertiary,
  '--bc-blue': frontDesignTokens.colorPrimary,
  '--bc-red': frontDesignTokens.colorError,
  '--bc-orange': frontDesignTokens.colorWarning,
  '--bc-yellow': '#fbbd08',
  '--bc-olive': '#b5cc18',
  '--bc-green': frontDesignTokens.colorSuccess,
  '--bc-teal': '#00b5ad',
  '--bc-violet': '#6435c9',
  '--bc-purple': frontDesignTokens.colorPurple,
  '--bc-pink': '#e03997',
  '--bc-brown': '#a5673f',
  '--bc-grey': '#767676',
  '--bc-gray': '#767676',
  '--bc-black': '#1b1c1d',
  '--bc-radius': `${frontDesignTokens.borderRadius}px`,
  '--bc-shadow': frontDesignTokens.shadow,
  '--bc-shadow-hover': frontDesignTokens.shadowHover,
  '--bc-font-sans': frontFontFamily,
  '--bc-font-headline-lg-size': `${frontTypographyTokens.headlineLg.fontSize}px`,
  '--bc-font-headline-lg-weight': String(frontTypographyTokens.headlineLg.fontWeight),
  '--bc-font-headline-lg-line-height': `${frontTypographyTokens.headlineLg.lineHeight}px`,
  '--bc-font-headline-lg-mobile-size': `${frontTypographyTokens.headlineLgMobile.fontSize}px`,
  '--bc-font-headline-lg-mobile-line-height': `${frontTypographyTokens.headlineLgMobile.lineHeight}px`,
  '--bc-font-headline-md-size': `${frontTypographyTokens.headlineMd.fontSize}px`,
  '--bc-font-headline-md-weight': String(frontTypographyTokens.headlineMd.fontWeight),
  '--bc-font-headline-md-line-height': `${frontTypographyTokens.headlineMd.lineHeight}px`,
  '--bc-font-body-lg-size': `${frontTypographyTokens.bodyLg.fontSize}px`,
  '--bc-font-body-lg-line-height': `${frontTypographyTokens.bodyLg.lineHeight}px`,
  '--bc-font-body-md-size': `${frontTypographyTokens.bodyMd.fontSize}px`,
  '--bc-font-body-md-line-height': `${frontTypographyTokens.bodyMd.lineHeight}px`,
  '--bc-font-label-lg-size': `${frontTypographyTokens.labelLg.fontSize}px`,
  '--bc-font-label-lg-weight': String(frontTypographyTokens.labelLg.fontWeight),
  '--bc-font-label-lg-line-height': `${frontTypographyTokens.labelLg.lineHeight}px`,
  '--bc-font-label-md-size': `${frontTypographyTokens.labelMd.fontSize}px`,
  '--bc-font-label-md-weight': String(frontTypographyTokens.labelMd.fontWeight),
  '--bc-font-label-md-line-height': `${frontTypographyTokens.labelMd.lineHeight}px`,
  '--bc-surface-hover': frontDesignTokens.colorSurfaceHover,
  '--bc-focus-ring': frontDesignTokens.focusRing
};

export function installFrontThemeCssVariables(root: HTMLElement = document.documentElement) {
  Object.entries(frontCssVariables).forEach(([name, value]) => root.style.setProperty(name, value));
}
